<template>
  <ui-badge
    v-if="compact"
    v-tooltip="compactTooltip"
    data-slot="rule-waiting-list-status"
    :variant="compactVariant"
    class="shrink-0"
  >
    {{ compactLabel }}
  </ui-badge>
  <div v-else data-slot="rule-waiting-list-status" :class="[ 'flex flex-col justify-center gap-0.5', inModal && 'items-start' ]">
    <div v-if="!entity.isHandled && !entity.is_current" class="text-xs text-muted-foreground italic">
      {{ notActualText }}
    </div>
    <template v-else>
      <div :class="[ 'flex items-center gap-2', inModal && 'w-full max-w-80' ]">
        <template v-if="isHandlingButtonVisible">
          <template v-if="isActivityManagementItem">
            <ui-button class="flex-1" :disabled="disabled" @click="() => activityManagementAccept()">
              <loader-circle-icon v-if="loadingButton === entity.id + '--accept'" class="animate-spin" />
              {{ tr('Accept') }}
            </ui-button>
            <ui-button
              variant="destructive"
              size="icon"
              :disabled="disabled"
              :aria-label="tr('Reject')"
              @click="() => activityManagementReject()"
            >
              <loader-circle-icon v-if="loadingButton === entity.id + '--reject'" class="animate-spin" />
              <trash-icon v-else />
            </ui-button>
          </template>
          <ui-button
            v-else
            class="flex-1"
            :disabled="loadingButton !== null || $root.isSupportMode"
            @click="() => overrideAi()"
          >
            <loader-circle-icon v-if="loadingButton === entity.id + '--override'" class="animate-spin" />
            {{ tr('Override') }}
          </ui-button>
        </template>

        <span
          v-else
          :class="[
            'inline-flex max-w-full min-w-0 items-center gap-2.5 rounded-2xl py-1.5 pr-4 pl-2.5 whitespace-nowrap',
            isRejected ? 'bg-destructive/10' : 'bg-primary/8',
          ]"
        >
          <circle-x-icon v-if="isRejected" class="size-5 shrink-0 text-destructive" />
          <circle-check-icon v-else class="size-5 shrink-0 text-primary" />
          <span class="flex min-w-0 flex-col gap-px leading-tight">
            <span class="truncate text-xs text-muted-foreground">
              {{ handledLabel }} ·
              <span v-tooltip="handledAtTooltip">{{ handledTimeAgo }}</span>
            </span>
            <span class="truncate text-xs text-foreground">{{ handledByLabel }}</span>
          </span>
        </span>
      </div>

      <div v-if="isHandlingButtonVisible" :class="[ 'text-xs text-muted-foreground', !inModal && 'max-md:text-center' ]">
        {{ pendingStatus }}
      </div>

      <ui-button
        v-if="isActivityManagementItem && isAcceptAfterRejectionVisible && inModal"
        variant="outline"
        class="mt-3"
        :disabled="disabled"
        @click="() => activityManagementAcceptAfterRejection()"
      >
        <loader-circle-icon v-if="loadingButton === entity.id + '--accept'" class="animate-spin" />
        {{ tr('Accept anyway') }}
      </ui-button>
    </template>
  </div>
</template>

<script>
import { CircleCheck, CircleX, LoaderCircle, Trash2 } from '@lucide/vue';

import { EVENT_BUS_EVENTS, RULE_ACTION, RULE_ACTIVITY_MANAGEMENT_INSTRUCTION } from '@/consts.js';
import PostAiProhibitedContent from '@/entities/PostAiProhibitedContent.js';
import Rule from '@/entities/Rule.js';
import RuleActivityManagementQueueItem from '@/entities/RuleActivityManagementQueueItem.js';
import RuleActivityManagementService from '@/services/RuleActivityManagement.js';
import RuleAiManagementService from '@/services/RuleAiManagement.js';
import { formatTime, formatToYMD, timeAgo } from '@/utils/date.js';
import EventBus from '@/utils/eventBus.js';

export default {
  name: 'RuleWaitingListStatus',
  components: {
    CircleCheckIcon: CircleCheck,
    CircleXIcon: CircleX,
    LoaderCircleIcon: LoaderCircle,
    TrashIcon: Trash2,
  },
  props: {
    rule: {
      type: Rule,
      required: true,
    },
    entity: {
      type: [ PostAiProhibitedContent, RuleActivityManagementQueueItem ],
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    inModal: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    // A status badge only; the actions live in the item's modal.
    compact: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  data: () => ({
    loadingButton: null,
  }),
  computed: {
    isActivityManagementItem() {
      return this.entity instanceof RuleActivityManagementQueueItem;
    },
    isAiProhibitedItem() {
      return this.entity instanceof PostAiProhibitedContent;
    },
    isHandlingButtonVisible() {
      return !this.entity.isHandled && !this.$root.loggedInUser.isRestrictedViewer;
    },
    isAcceptAfterRejectionVisible() {
      return this.entity.canBeAcceptedAfterRejection && !this.$root.loggedInUser.isRestrictedViewer;
    },
    handledByLabel() {
      if (this.entity.handled_by) {
        return this.tr('by :name', { ':name': this.entity.handled_by.fullname });
      }

      const label = this.tr('automatically');

      return label.charAt(0).toUpperCase() + label.slice(1);
    },
    notActualText() {
      return this.isActivityManagementItem
        ? this.tr('The item affected by the activity is no longer available.')
        : this.tr('The result is no longer relevant because the automation or the entry has been modified since the review.');
    },
    isRejected() {
      return this.isActivityManagementItem && this.entity.isRejected;
    },
    handledLabel() {
      if (this.isAiProhibitedItem) {
        return this.tr('Overrided');
      }

      switch (this.entity.instruction) {
        case RULE_ACTIVITY_MANAGEMENT_INSTRUCTION.ACCEPT:
          return this.tr('Accepted');

        case RULE_ACTIVITY_MANAGEMENT_INSTRUCTION.REJECT:
          return this.tr('Rejected');
      }

      return null;
    },
    pendingStatus() {
      if (this.isAiProhibitedItem) {
        return this.tr('The content does not comply with Meta\'s policy');
      }

      if (this.rule.action === RULE_ACTION.START || this.rule.action === RULE_ACTION.SCALING_UP) {
        return this.tr('The ad launch is awaiting your approval');
      }

      return this.tr('The ad stoppage is awaiting your approval');
    },
    compactLabel() {
      if (!this.entity.isHandled && !this.entity.is_current) {
        return this.tr('Outdated');
      }

      return this.entity.isHandled ? this.handledLabel : this.tr('Awaiting decision');
    },
    compactTooltip() {
      if (!this.entity.isHandled && !this.entity.is_current) {
        return this.notActualText;
      }

      return this.entity.isHandled ? this.handledByLabel + ' · ' + this.handledTimeAgo : this.pendingStatus;
    },
    compactVariant() {
      if (!this.entity.isHandled) {
        return this.entity.is_current ? 'warning' : 'secondary';
      }

      return this.isRejected ? 'destructive' : 'success';
    },
    handledTimeAgo() {
      return this.entity.handled_at ? timeAgo(this.entity.handled_at, this.tr) : null;
    },
    handledAtTooltip() {
      return this.entity.handled_at ? formatToYMD(this.entity.handled_at) + ' ' + formatTime(this.entity.handled_at) : null;
    },
  },
  watch: {
    loadingButton(val) {
      this.$emit('loading-button', val);
    },
  },
  methods: {
    activityManagementAccept() {
      this.activityManagementInstruct(RULE_ACTIVITY_MANAGEMENT_INSTRUCTION.ACCEPT);
    },
    activityManagementReject() {
      this.activityManagementInstruct(RULE_ACTIVITY_MANAGEMENT_INSTRUCTION.REJECT);
    },
    activityManagementAcceptAfterRejection() {
      if (this.disabled) {
        return;
      }

      this.$confirmation.open({
        title: this.tr('Accept rejected activity'),
        content: this.tr('The activity will be executed within a few minutes based on the original decision of the automation, even if the conditions of the automation are no longer met.'),
        labelCancel: this.tr('Cancel'),
        labelConfirm: this.tr('Accept'),
        confirm: () => this.activityManagementInstruct(RULE_ACTIVITY_MANAGEMENT_INSTRUCTION.ACCEPT, true),
      });
    },
    activityManagementInstruct(instruction, overrideRejection = false) {
      if (this.disabled) {
        return null;
      }

      if (
        instruction !== RULE_ACTIVITY_MANAGEMENT_INSTRUCTION.ACCEPT
        && instruction !== RULE_ACTIVITY_MANAGEMENT_INSTRUCTION.REJECT
      ) {
        return null;
      }
      this.loadingButton = this.entity.id + '--' + instruction;

      return RuleActivityManagementService.instruct(this.rule.id, this.entity.id, instruction, overrideRejection)
        .then(resp => {
          this.entity.updateWith(resp);

          this.$toast.clear();
          if (instruction === RULE_ACTIVITY_MANAGEMENT_INSTRUCTION.ACCEPT) {
            this.$toast.success(this.tr('Activity accepted successfully. The activity will run in a few minutes.'));
            EventBus.$emit(EVENT_BUS_EVENTS.WAITING_ACTIVITY_ACCEPTED, resp);
          }
          else if (instruction === RULE_ACTIVITY_MANAGEMENT_INSTRUCTION.REJECT) {
            this.$toast.success(this.tr('Activity rejected successfully.'));
            EventBus.$emit(EVENT_BUS_EVENTS.WAITING_ACTIVITY_REJECTED, resp);
          }

          return resp;
        })
        .catch(err => this.error(err))
        .finally(() => this.loadingButton = null);
    },
    overrideAi() {
      if (this.$root.isSupportMode) {
        return;
      }

      if (this.loadingButton !== null) {
        this.infoToast(this.tr('Please wait until the previous operation is completed.'));
        return;
      }

      this.loadingButton = this.entity.id + '--override';

      RuleAiManagementService.overrideResult(this.rule.id, this.entity)
        .then(resp => {
          this.entity.updateWith(resp);

          this.$toast.clear();
          this.$toast.success(this.tr('Decision successfully overrided. During the next run of the automation, the automation will attempt to create the advertisement.'));

          EventBus.$emit(EVENT_BUS_EVENTS.PROHIBITED_CONTENT_OVERRIDDEN, resp);

          return resp;
        })
        .catch(err => this.error(err))
        .finally(() => this.loadingButton = null);
    },
  },
};
</script>
